import React, { useState, useEffect } from 'react';

export function TextField({ label, value, onChange, multiline = false, placeholder = '', testid }) {
  return (
    <label className="block">
      {label && <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">{label}</span>}
      {multiline ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary" data-testid={testid} />
      ) : (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary" data-testid={testid} />
      )}
    </label>
  );
}

// numberToString — format a numeric value for the NumberField buffer.
// Returns '' for null/undefined/NaN; otherwise uses Number.toString() which
// naturally drops trailing zeros (e.g. (0.5).toString() === '0.5').
function numberToString(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  return String(n);
}

// NumberField — numeric admin input with a local string buffer.
// Lets users type freely (including '0.', '.5', empty, etc.) without per-
// keystroke coercion. Commits a clamped numeric value to upstream state on
// blur or Enter. Reverts to the upstream value on Escape or on empty/invalid
// commit. Re-syncs from upstream `value` when the input is not focused.
export function NumberField({ label, value, min = -Infinity, max = Infinity, step, onCommit, testid, disabled = false, className = '' }) {
  const [buf, setBuf] = useState(() => numberToString(value));
  const [focused, setFocused] = useState(false);

  // Re-sync the local buffer from upstream when not focused (handles save
  // round-trips, external resets, seed bumps, etc. — never yanks an in-
  // progress edit).
  useEffect(() => {
    if (!focused) setBuf(numberToString(value));
  }, [value, focused]);

  const commit = (raw) => {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '' || trimmed === '.' || trimmed === '-' || trimmed === '-.') {
      setBuf(numberToString(value));
      return;
    }
    const n = parseFloat(trimmed);
    if (Number.isNaN(n)) {
      setBuf(numberToString(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, n));
    setBuf(numberToString(clamped));
    onCommit(clamped);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(buf);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setBuf(numberToString(value));
    }
  };

  return (
    <label className={`block ${className}`}>
      {label && <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">{label}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        step={step}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={(e) => setBuf(e.target.value)}
        onBlur={() => { setFocused(false); commit(buf); }}
        onKeyDown={handleKey}
        className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary disabled:opacity-50"
        data-testid={testid}
      />
    </label>
  );
}

export function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="font-body text-[13px] text-ink-primary">{label}</span>
    </label>
  );
}

export function Section({ title, children, action }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold uppercase text-ink-primary text-[20px] tracking-tightish">{title}</h2>
        {action}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function Row({ children, className = '' }) {
  return <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>{children}</div>;
}

export function SaveBar({ onSave, saving, saved }) {
  return (
    <div className="sticky bottom-0 -mx-5 md:-mx-8 -mb-5 md:-mb-8 mt-8 bg-bg-elevated border-t border-border-line p-4 flex items-center justify-end gap-3">
      {saved && <span className="font-body text-[12px] text-green-700">Saved ✓</span>}
      <button onClick={onSave} disabled={saving} className="btn-primary text-[12px] !py-2.5 !px-5" data-testid="save-button">
        {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );
}

export function PillSelect({ label, value, options, onChange, testid }) {
  return (
    <div>
      {label && <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">{label}</label>}
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((opt) => {
          const key = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + (active ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}
              data-testid={testid ? `${testid}-${key}` : undefined}
            >
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// v3.15 — EyebrowSizeField
// ------------------------------------------------------------
// Purpose-built control for the 7 section "orange eyebrow"
// labels (Experiences, Speakers, Partners, Gallery,
// Registration, Fast Track, FAQ). Strict integer input in the
// tested safe range [16, 48].
//
// Contract:
//   • value === null | undefined  →  the parent has NO override
//     stored; the public site falls back to the 26px CSS default.
//   • value: integer in [16, 48]  →  operator has an explicit
//     override; that exact size is used on the public site.
//
// Validation policy (NON-negotiable):
//   • Empty commit  →  onCommit(null)  — the override is REMOVED
//     from the outgoing settings payload (no silent 26 write).
//   • Any non-integer input (e.g. "17.5", "17abc", "-5", "17 ")
//     →  REJECTED. onCommit is NOT called. Inline error shown.
//     The previously-stored value stays intact upstream.
//   • Integer outside [16, 48]  →  REJECTED. Same handling as
//     above. NEVER silently clamped to 16 or 48.
//   • Integer in [16, 48]  →  onCommit(n) with the integer.
//
// This validator is the ONLY line of defense for the permissive
// Dict[str, Any] endpoints (sections-meta, registration). For
// Fast Track the strict Pydantic model on the server enforces the
// same rules with an HTTP 422 on any violation.
export function EyebrowSizeField({ label = 'Eyebrow Size (px)', value, onCommit, testid, hint }) {
  const initial = (typeof value === 'number' && Number.isInteger(value)) ? String(value) : '';
  const [buf, setBuf] = useState(initial);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState('');

  // Re-sync buffer from upstream when the upstream `value` changes AND
  // the input is not focused (handles save round-trips, external clears,
  // tab switches, etc.). CRITICAL: `focused` is NOT in the dependency
  // array — otherwise the blur transition itself would re-fire this
  // effect and wipe out a fresh validation error before the user could
  // read it.
  useEffect(() => {
    if (!focused) {
      setBuf((typeof value === 'number' && Number.isInteger(value)) ? String(value) : '');
      setError('');
    }
  }, [value]);

  const commit = (raw) => {
    const trimmed = (raw ?? '').trim();
    // Empty → remove override → parent MUST omit the key from PUT payload.
    if (trimmed === '') {
      setError('');
      onCommit(null);
      return;
    }
    // Strict integer pattern: rejects "17.5", "17abc", "-5", " 17", "+17", etc.
    if (!/^\d+$/.test(trimmed)) {
      setError('Enter a whole number between 16 and 48, or leave blank for the default (26).');
      return; // NO commit — invalid input, upstream stored value unchanged.
    }
    const n = parseInt(trimmed, 10);
    if (n < 16 || n > 48) {
      setError('Out of range. Enter a whole number between 16 and 48.');
      return; // NO commit — never silently clamped.
    }
    setError('');
    onCommit(n);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(buf);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setBuf((typeof value === 'number' && Number.isInteger(value)) ? String(value) : '');
      setError('');
    }
  };

  const clear = () => {
    setBuf('');
    setError('');
    onCommit(null);
  };

  return (
    <div className="mt-2">
      <label className="block">
        <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">{label}</span>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            inputMode="numeric"
            value={buf}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); commit(buf); }}
            onChange={(e) => { setBuf(e.target.value); if (error) setError(''); }}
            onKeyDown={handleKey}
            placeholder="26 (default)"
            className={`w-32 border rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none ${error ? 'border-brand-red focus:border-brand-red' : 'border-border-line focus:border-ink-primary'}`}
            data-testid={testid}
            aria-invalid={error ? true : false}
          />
          <button
            type="button"
            onClick={clear}
            className="btn-secondary text-[11px] !py-1.5 !px-3"
            data-testid={testid ? `${testid}-clear` : undefined}
          >
            Clear
          </button>
          <span className="font-body text-[11px] text-ink-soft">px · whole number · 16–48 · blank = default 26 · phones (≤ 767px) render max 30</span>
        </div>
      </label>
      {error && (
        <p className="mt-1 font-body text-[11px] text-brand-red" data-testid={testid ? `${testid}-error` : undefined}>
          {error}
        </p>
      )}
      {hint && !error && (
        <p className="mt-1 font-body text-[11px] text-ink-muted">{hint}</p>
      )}
    </div>
  );
}

